import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TOPICS_DATA, TOPICS_ICONS } from '../constants/tagConstants';

function TagFilter({ onTagSelect }) {
  const [selectedTags, setSelectedTags] = useState([]);
  const [activeTopic, setActiveTopic] = useState(null);

  const handleTopicClick = (topic) => {
    setActiveTopic(activeTopic === topic ? null : topic);
  };

  const handleTagSelect = (path) => {
    const newTags = selectedTags.includes(path)
      ? selectedTags.filter(t => t !== path)
      : [...selectedTags, path];
    setSelectedTags(newTags);
    onTagSelect(newTags);
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = selectedTags.filter(tag => tag !== tagToRemove);
    setSelectedTags(newTags);
    onTagSelect(newTags);
  };

  const handleRemoveAllTags = () => {
    setSelectedTags([]);
    onTagSelect([]);
    setActiveTopic(null);
  };

  const handleBack = () => {
    if (activeTopic) setActiveTopic(null);
  };

  const getSelectedCount = (topic) => {
    return selectedTags.filter(tag => {
      const [tagTopic] = tag.split('/');
      return tagTopic === topic;
    }).length;
  };

  const SelectButton = ({ path, isSelected }) => (
    <motion.button
      onClick={(e) => { e.stopPropagation(); handleTagSelect(path); }}
      className={`ml-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${
        isSelected
          ? 'bg-purple-600 text-white hover:bg-purple-700'
          : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
      }`}
      whileTap={{ scale: 0.95 }}
    >
      {isSelected ? 'On' : 'Off'}
    </motion.button>
  );

  return (
    <div className="space-y-3">
      {/* Selected tags */}
      {selectedTags.length > 0 && (
        <motion.div
          className="flex flex-wrap gap-1.5 p-3 glass-card rounded-lg"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {selectedTags.map((tag) => {
            const parts = tag.split('/');
            return (
              <span key={tag} className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-semibold bg-white/90 text-purple-700">
                {parts.length > 1 ? parts[1] : parts[0]}
                {parts.length > 1 && (
                  <>
                    <span className="mx-1 text-gray-300">in</span>
                    <span className="text-gray-400">{parts[0]}</span>
                  </>
                )}
                <button onClick={() => handleRemoveTag(tag)} className="ml-1.5 text-gray-300 hover:text-red-500 transition-colors font-bold">×</button>
              </span>
            );
          })}
          <button onClick={handleRemoveAllTags}
            className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-red-300 hover:text-red-400 hover:bg-red-500/10 transition-colors">
            Clear
          </button>
        </motion.div>
      )}

      {/* Breadcrumb */}
      {activeTopic && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 text-xs">
          <button onClick={handleBack} className="text-yellow-300/70 hover:text-yellow-300 transition-colors font-semibold">
            &lt;- Back
          </button>
          <span className="text-white/20">|</span>
          <span className="text-white/60 text-[11px]">{activeTopic}</span>
        </motion.div>
      )}

      {/* Content */}
      <AnimatePresence mode="wait">
        {!activeTopic ? (
          <motion.div key="topics" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(TOPICS_DATA.topics).map(([topic]) => {
              const IconComponent = TOPICS_ICONS[topic];
              const selectedCount = getSelectedCount(topic);
              const isSelected = selectedTags.includes(topic);
              return (
                <motion.div key={topic}
                  className={`p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                    selectedCount > 0 ? 'border-purple-200 bg-purple-50' : 'border-gray-100 bg-white hover:border-purple-200 hover:shadow-card'
                  }`}
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-center justify-between">
                    <button onClick={() => handleTopicClick(topic)} className="flex items-center text-xs font-semibold text-gray-800 flex-grow text-left">
                      {IconComponent && <IconComponent className="w-3.5 h-3.5 mr-2 text-purple-400" />}
                      <span>{topic}</span>
                      {selectedCount > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-purple-100 text-purple-700 rounded font-bold tabular-nums">{selectedCount}</span>
                      )}
                    </button>
                    <div className="flex items-center">
                      <SelectButton path={topic} isSelected={isSelected} />
                      <span className="ml-1.5 text-gray-200 text-xs">→</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div key="subtopics" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-1.5">
            {Object.entries(TOPICS_DATA.topics[activeTopic]).map(([subtopic]) => {
              const fullPath = `${activeTopic}/${subtopic}`;
              const isSelected = selectedTags.includes(fullPath);
              return (
                <motion.div key={subtopic}
                  className={`p-3 rounded-lg border transition-all ${isSelected ? 'border-purple-200 bg-purple-50' : 'border-gray-100 bg-white hover:border-purple-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">{subtopic}</span>
                    <SelectButton path={fullPath} isSelected={isSelected} />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TagFilter;
